/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { MessageFilterState } from "@/types";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Filter } from "lucide-react";
import { useCallback } from "react";
import { DateRange } from "react-day-picker";

interface MessageFiltersProps {
  filters: MessageFilterState;
  onFiltersChange: (filters: MessageFilterState) => void;
  onClearFilters: () => void;
  className?: string;
}

export function MessageFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  className,
}: MessageFiltersProps) {
  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (!range) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { dateRange: _, ...rest } = filters;
        onFiltersChange(rest);
        return;
      }

      onFiltersChange({
        ...filters,
        dateRange: {
          from: range.from || new Date(),
          to: range.to || new Date(),
        },
      });
    },
    [filters, onFiltersChange],
  );

  const handleAttachmentsChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        onFiltersChange({ ...filters, hasAttachments: true });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { hasAttachments: _, ...rest } = filters;
        onFiltersChange(rest);
      }
    },
    [filters, onFiltersChange],
  );

  const hasActiveFilters = filters.dateRange || filters.hasAttachments;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 gap-1 px-2",
            hasActiveFilters && "bg-accent text-accent-foreground",
            className,
          )}
        >
          <Filter className="h-4 w-4" />
          {hasActiveFilters && (
            <span className="bg-primary text-primary-foreground flex h-4 w-4 items-center justify-center rounded-full text-[10px] leading-3 font-medium">
              {Object.keys(filters).length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="end">
        <div className="space-y-4">
          <div>
            <div className="space-y-2">
              <div className="font-medium">Date Range</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.dateRange && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange?.from ? (
                      filters.dateRange.to ? (
                        <>
                          {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                          {format(filters.dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(filters.dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={filters.dateRange?.from}
                    selected={{
                      from: filters.dateRange?.from,
                      to: filters.dateRange?.to,
                    }}
                    onSelect={handleDateRangeChange}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Separator />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="attachments"
              checked={filters.hasAttachments}
              onCheckedChange={handleAttachmentsChange}
            />
            <label
              htmlFor="attachments"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Only show messages with attachments
            </label>
          </div>

          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
          >
            Clear All Filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
